import { getHexTileMapKey } from '../hexmap/model/keys';
import { getTile } from '../hexmap/model/map';
import { exploredLookup } from './explored';
import { covers } from './footprint';
import { buildsFor } from './occupancy';
import { tileResources } from './systems/economy';
import type { Building, Cell, EntityId, PlayerId, SimContext, SimState, Unit } from './types';
import { computeVision } from './vision';

/**
 * What an `assign` command sends workers to (commands.ts `handleAssign`):
 * resolved from the target alone, then checked per unit. Like every command
 * it never depends on state hidden from its sender: a wild tile counts when
 * it's explored and its terrain carries a resource (not by what's left on
 * it: a dry one just leaves the worker idle), and a rival building on the
 * tile only matters while the sender sees it.
 */

type AssignJob =
  | { kind: 'build'; building: Building }
  // `target`: the providing building's own tile, or the wild tile.
  | { kind: 'gather'; target: Cell };

type AssignRejection =
  /** The tile is unexplored. */
  | 'unexplored'
  /** A rival building the sender sees stands there. */
  | 'rival'
  /** Nothing to work there (no own site or providing building, no resource). */
  | 'nothing'
  /** None of the units can do that job. */
  | 'cantWork';

type AssignCheck = { ok: true; job: AssignJob } | { ok: false; reason: AssignRejection };

/** The job an own building offers, if any. */
function buildingJob(ctx: SimContext, b: Building): AssignJob | null {
  const def = ctx.rules.buildings[b.type];
  if (!def) return null;
  if (b.buildProgress < 1) return def.consumesBuilder ? null : { kind: 'build', building: b };
  return def.provides ? { kind: 'gather', target: { q: b.pos.q, r: b.pos.r } } : null;
}

/** The job at `target` (an own building's id, or a tile) for `playerId`'s workers. */
function resolveJob(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  target: Cell | EntityId
): AssignCheck {
  if (typeof target === 'string') {
    const b = state.buildings[target];
    // A rival's building (or none) is the same answer.
    const job = b && b.owner === playerId ? buildingJob(ctx, b) : null;
    return job ? { ok: true, job } : { ok: false, reason: 'nothing' };
  }
  if (!getTile(ctx.terrain, target)) return { ok: false, reason: 'nothing' };
  if (!exploredLookup(ctx.terrain, state.explored[playerId])(getHexTileMapKey(target))) {
    return { ok: false, reason: 'unexplored' };
  }
  const cover = Object.values(state.buildings).find((b) => covers(b, target));
  if (cover?.owner === playerId) {
    const job = buildingJob(ctx, cover);
    return job ? { ok: true, job } : { ok: false, reason: 'nothing' };
  }
  if (cover && computeVision(state, ctx.rules, playerId).has(getHexTileMapKey(target))) {
    return { ok: false, reason: 'rival' };
  }
  // Wild (or a rival building out of sight, which looks the same).
  return tileResources(state, ctx, target)
    ? { ok: true, job: { kind: 'gather', target: { q: target.q, r: target.r } } }
    : { ok: false, reason: 'nothing' };
}

/** Whether `unit` can do `job`: gatherers gather, builders of that building's type build. */
function canTakeJob(ctx: SimContext, unit: Unit, job: AssignJob): boolean {
  const def = ctx.rules.units[unit.type];
  if (!def) return false;
  if (job.kind === 'gather') return def.actions.includes('gather');
  const builtBy = ctx.rules.buildings[job.building.type]?.builtBy;
  return builtBy !== undefined && buildsFor(ctx.rules, unit.type, builtBy);
}

/** The shared check behind `assign`: the job at `target`, and that some of `unitIds` can take it. */
function canAssign(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  unitIds: readonly EntityId[],
  target: Cell | EntityId
): AssignCheck {
  const check = resolveJob(state, ctx, playerId, target);
  if (!check.ok) return check;
  const any = unitIds.some((id) => {
    const u = state.units[id];
    return u && u.owner === playerId && canTakeJob(ctx, u, check.job);
  });
  return any ? check : { ok: false, reason: 'cantWork' };
}

export type { AssignCheck, AssignJob, AssignRejection };
export { canAssign, canTakeJob, resolveJob };
